import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plug, CheckCircle2, RefreshCw, Link2, PlugZap, ExternalLink, Info } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { t: "Create a free HubRise account", d: "Go to manager.hubrise.com and sign up. HubRise sits between DineFlo and delivery marketplaces." },
  { t: "Add your Location", d: "In HubRise, create an Account and a Location for your restaurant (name, address, currency GBP)." },
  { t: "Connect your delivery channels", d: "In HubRise → Connections → View available apps, connect Just Eat, Uber Eats and/or Deliveroo to your Location. Orders from these channels will then reach DineFlo." },
  { t: "Click “Connect HubRise” below", d: "You'll be sent to HubRise to approve DineFlo, pick the Location & catalog, then bounced straight back here — connected." },
  { t: "Push your menu & go live", d: "After connecting, hit “Push menu to HubRise” so your catalog is in sync. New channel orders then flow into your Orders & Kitchen automatically." },
];

export default function HubRiseChannel({ rid }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/hubrise/status`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [rid]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/restaurants/${rid}/hubrise/connect`, {
        params: { redirect_base: window.location.origin },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start HubRise connection");
      setBusy(false);
    }
  };

  const pushMenu = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurants/${rid}/hubrise/push-menu`);
      data.ok ? toast.success(`Menu pushed to HubRise (${data.pushed_products} items)`) : toast.error(data.reason || "Push failed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); }
    setBusy(false);
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post(`/restaurants/${rid}/hubrise/disconnect`);
      toast.success("HubRise disconnected");
      load();
    } catch { toast.error("Could not disconnect"); }
    setBusy(false);
  };

  if (!status) return null;
  const connected = status.connected;

  return (
    <Card className="p-6 rounded-2xl max-w-3xl mt-4" data-testid="hubrise-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center"><Plug className="h-5 w-5 text-primary" /></div>
          <div>
            <p className="font-display font-semibold text-lg">HubRise</p>
            <p className="text-sm text-muted-foreground">Just Eat · Uber Eats · Deliveroo — one connection</p>
          </div>
        </div>
        {connected ? (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="hubrise-connected-badge"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Connected</Badge>
        ) : (
          <Badge variant="outline" className="bg-stone-100 text-stone-600" data-testid="hubrise-status-badge">Not connected</Badge>
        )}
      </div>

      {!status.platform_configured && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 mt-3 flex gap-2" data-testid="hubrise-not-configured">
          <Info className="h-4 w-4 shrink-0 mt-0.5" /> HubRise isn't enabled on this platform yet. Please ask your DineFlo administrator to add the HubRise credentials.
        </div>
      )}

      {!connected && status.platform_configured && (
        <>
          <div className="rounded-xl bg-secondary/60 p-4 mt-4">
            <p className="text-sm font-semibold mb-3">How to set up HubRise (one-time)</p>
            <ol className="space-y-3">
              {STEPS.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span><span className="font-medium">{s.t}.</span> <span className="text-muted-foreground">{s.d}</span></span>
                </li>
              ))}
            </ol>
            <a href="https://manager.hubrise.com/signup" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-3 hover:underline" data-testid="hubrise-signup-link">
              Open HubRise <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <Button className="h-12 mt-4 gap-2" onClick={connect} disabled={busy} data-testid="hubrise-connect-btn">
            <Link2 className="h-4 w-4" /> {busy ? "Redirecting…" : "Connect HubRise"}
          </Button>
        </>
      )}

      {connected && (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Account</p><p className="font-medium truncate" data-testid="hubrise-account">{status.account_name || "—"}</p></div>
            <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Location</p><p className="font-medium truncate" data-testid="hubrise-location">{status.location_name || "—"}</p></div>
            <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Catalog</p><p className="font-medium truncate" data-testid="hubrise-catalog">{status.catalog_name || "—"}</p></div>
          </div>
          <div className="rounded-lg bg-secondary p-3 mt-3 text-xs text-muted-foreground">
            {status.menu_last_synced_at ? `Menu last synced ${new Date(status.menu_last_synced_at).toLocaleString()}.` : "Menu not pushed yet — push it so HubRise has your catalog."}
            {status.last_failed_at && <span className="text-red-600 block mt-1">Last HubRise request failed — try reconnecting.</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-3">Incoming channel orders now arrive automatically in your Orders & Kitchen. Order status changes here are pushed back to HubRise.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button className="h-11 gap-2" onClick={pushMenu} disabled={busy} data-testid="hubrise-push-menu-btn"><RefreshCw className="h-4 w-4" /> Push menu to HubRise</Button>
            <Button variant="outline" className="h-11 gap-2" onClick={connect} disabled={busy} data-testid="hubrise-reconnect-btn"><PlugZap className="h-4 w-4" /> Reconnect</Button>
            <Button variant="outline" className="h-11 gap-2 text-red-600 hover:text-red-700" onClick={disconnect} disabled={busy} data-testid="hubrise-disconnect-btn">Disconnect</Button>
          </div>
        </>
      )}
    </Card>
  );
}
